/**
 * SubscriptionExpiracaoSection - Seção colapsável de Expiração da Cobrança
 *
 * Define quantos dias após o vencimento uma cobrança permanece ativa para
 * pagamento (1–60 dias). Após o prazo, a cobrança expira automaticamente.
 *
 * Persistência: coluna `dias_expiracao_pos_vencimento` (INTEGER, default 60).
 * Validação 1..60 aplicada também no backend (trigger + RPCs).
 *
 * @version 1.0.0
 * RELEASE: 2026-06-10 v1.0.0 - Criação inicial.
 */
import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ChevronDown, Hourglass, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export const DIAS_EXPIRACAO_DEFAULT = 60;
export const DIAS_EXPIRACAO_MIN = 1;
export const DIAS_EXPIRACAO_MAX = 60;

interface Props {
  value: number;
  onChange?: (value: number) => void;
  readOnly?: boolean;
  defaultOpen?: boolean;
}

/** Clampa para a faixa permitida (1..60). */
export function clampDiasExpiracao(n: number): number {
  if (!Number.isFinite(n)) return DIAS_EXPIRACAO_DEFAULT;
  if (n < DIAS_EXPIRACAO_MIN) return DIAS_EXPIRACAO_MIN;
  if (n > DIAS_EXPIRACAO_MAX) return DIAS_EXPIRACAO_MAX;
  return Math.floor(n);
}

export function SubscriptionExpiracaoSection({
  value,
  onChange,
  readOnly = false,
  defaultOpen,
}: Props) {
  const [open, setOpen] = useState(defaultOpen ?? !readOnly);
  const dias = value ?? DIAS_EXPIRACAO_DEFAULT;
  const invalid = dias < DIAS_EXPIRACAO_MIN || dias > DIAS_EXPIRACAO_MAX;

  return (
    <section className="rounded-lg border border-border/50">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="w-full flex items-center gap-2 px-3 py-2.5 group focus:outline-none">
          <Hourglass className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Expiração da cobrança</h3>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground px-1.5 py-0.5 rounded bg-muted">
            {dias} dias
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 ml-auto text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-3 pb-3 pt-1 space-y-2">
            <Label className="text-xs">
              Dias após o vencimento até a cobrança expirar
            </Label>
            {readOnly ? (
              <p className="text-sm font-medium">
                Cobrança expira <strong>{dias} dia{dias === 1 ? '' : 's'}</strong> após o vencimento.
              </p>
            ) : (
              <div className="flex items-center gap-2 max-w-[200px]">
                <Input
                  type="number"
                  min={DIAS_EXPIRACAO_MIN}
                  max={DIAS_EXPIRACAO_MAX}
                  value={dias}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    onChange?.(Number.isNaN(n) ? DIAS_EXPIRACAO_DEFAULT : n);
                  }}
                  onBlur={(e) => {
                    const n = parseInt(e.target.value, 10);
                    onChange?.(clampDiasExpiracao(Number.isNaN(n) ? DIAS_EXPIRACAO_DEFAULT : n));
                  }}
                  className="h-9"
                />
                <span className="text-xs text-muted-foreground">dias</span>
              </div>
            )}
            {invalid && !readOnly && (
              <p className="text-[11px] text-destructive flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Informe um valor entre {DIAS_EXPIRACAO_MIN} e {DIAS_EXPIRACAO_MAX} dias.
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              Após este prazo a cobrança expira automaticamente e não aceita novos pagamentos.
              Máximo permitido: {DIAS_EXPIRACAO_MAX} dias.
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

export default SubscriptionExpiracaoSection;
